import { useEffect, useMemo, useState } from 'react';
import patientHomeData from '../mocks/patientHome.json';
import { useAuth } from '../auth/useAuth';
import {
  GAP_ANALYSIS_UPDATED_EVENT,
  loadSharedGapQuestionsForPatient,
  type SharedGapQuestionRecord,
} from '../utils/patientGapAnalysisStore';

export interface PatientNutritionNotification {
  id: string;
  kind: 'QUESTION' | 'INSIGHT';
  title: string;
  message: string;
  createdAt: string;
  href: string;
  actionLabel: string;
}

const buildQuestionNotification = (
  record: SharedGapQuestionRecord
): PatientNutritionNotification => ({
  id: `question-${record.question.id}`,
  kind: 'QUESTION',
  title: 'Nutrition team question waiting',
  message: record.question.question,
  createdAt: record.question.sharedAt ?? record.analysisCreatedDate,
  href: '/patient/questions-from-nutritionist',
  actionLabel: 'Respond now',
});

const buildInsightNotification = (insight: (typeof patientHomeData.insights)[number]): PatientNutritionNotification => ({
  id: `insight-${insight.id}`,
  kind: 'INSIGHT',
  title: insight.title,
  message: insight.summary,
  createdAt: insight.createdAt,
  href: '/patient/home',
  actionLabel: 'View update',
});

export const usePatientNutritionNotifications = () => {
  const { user } = useAuth();
  const [pendingQuestions, setPendingQuestions] = useState<SharedGapQuestionRecord[]>([]);

  useEffect(() => {
    const refreshNotifications = () => {
      setPendingQuestions(
        loadSharedGapQuestionsForPatient(user?.id, user?.displayName).filter(
          (record) => record.question.patientStatus === 'PENDING_PATIENT'
        )
      );
    };

    refreshNotifications();
    window.addEventListener('storage', refreshNotifications);
    window.addEventListener(GAP_ANALYSIS_UPDATED_EVENT, refreshNotifications);

    return () => {
      window.removeEventListener('storage', refreshNotifications);
      window.removeEventListener(GAP_ANALYSIS_UPDATED_EVENT, refreshNotifications);
    };
  }, [user?.displayName, user?.id]);

  const unreadInsights = useMemo(
    () => patientHomeData.insights.filter((insight) => !insight.viewedByPatient),
    []
  );

  const notifications = useMemo(
    () => [
      ...pendingQuestions.map(buildQuestionNotification),
      ...unreadInsights.map(buildInsightNotification),
    ].sort(
      (left, right) =>
        new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()
    ),
    [pendingQuestions, unreadInsights]
  );

  return {
    notifications,
    notificationCount: notifications.length,
    pendingQuestionCount: pendingQuestions.length,
    unreadInsightCount: unreadInsights.length,
  };
};
